// Recurring tasks (PL-5): a small RRULE subset. Strings are canonical so the
// API can validate them byte for byte (apps/api/internal/tasks/recurrence.go):
//
//   FREQ=DAILY                          every day
//   FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR    every weekday
//   FREQ=WEEKLY                         every week, same weekday
//   FREQ=MONTHLY[;BYMONTHDAY=d]         every month, day d (default: the current due day)
//   ...;INTERVAL=n                      every n days/weeks/months, n = 2..99 (1 is omitted)
//
// Dates are Kampala calendar days ("YYYY-MM-DD"). All arithmetic runs on UTC
// day numbers, so the device's timezone can never shift a day.

export type Freq = "daily" | "weekdays" | "weekly" | "monthly";

export type Rule = {
	freq: Freq;
	/** 1..99 */
	interval: number;
	/** Monthly only: 1..31, clamped to the month's last day. */
	byMonthDay?: number;
};

const WEEKDAYS = "MO,TU,WE,TH,FR";
const MAX_INTERVAL = 99;

const CANONICAL =
	/^FREQ=(DAILY|WEEKLY|MONTHLY)(?:;(BYDAY=MO,TU,WE,TH,FR|BYMONTHDAY=([1-9]|[12]\d|3[01])))?(?:;INTERVAL=([2-9]|[1-9]\d))?$/;

/** Parses a canonical rule; anything else (including non-canonical RRULEs) is null. */
export function parseRule(rule: string | null | undefined): Rule | null {
	if (!rule) return null;
	const m = CANONICAL.exec(rule);
	if (!m) return null;
	const [, freq, modifier, monthDay, interval] = m;
	const n = interval ? Number(interval) : 1;

	if (freq === "DAILY") return modifier ? null : { freq: "daily", interval: n };
	if (freq === "WEEKLY") {
		if (monthDay) return null;
		return { freq: modifier ? "weekdays" : "weekly", interval: n };
	}
	if (modifier && !monthDay) return null; // MONTHLY;BYDAY=...
	return monthDay
		? { freq: "monthly", interval: n, byMonthDay: Number(monthDay) }
		: { freq: "monthly", interval: n };
}

/** The canonical string for a rule; throws on out-of-range values. */
export function formatRule(rule: Rule): string {
	if (!Number.isInteger(rule.interval) || rule.interval < 1 || rule.interval > MAX_INTERVAL) {
		throw new RangeError(`interval must be 1-${MAX_INTERVAL}`);
	}
	let s: string;
	switch (rule.freq) {
		case "daily":
			s = "FREQ=DAILY";
			break;
		case "weekdays":
			s = `FREQ=WEEKLY;BYDAY=${WEEKDAYS}`;
			break;
		case "weekly":
			s = "FREQ=WEEKLY";
			break;
		case "monthly": {
			s = "FREQ=MONTHLY";
			const d = rule.byMonthDay;
			if (d !== undefined) {
				if (!Number.isInteger(d) || d < 1 || d > 31)
					throw new RangeError("byMonthDay must be 1-31");
				s += `;BYMONTHDAY=${d}`;
			}
			break;
		}
	}
	return rule.interval > 1 ? `${s};INTERVAL=${rule.interval}` : s;
}

const UNIT: Record<Freq, [string, string]> = {
	daily: ["day", "days"],
	weekdays: ["week", "weeks"],
	weekly: ["week", "weeks"],
	monthly: ["month", "months"],
};

/** Plain English for the UI: "Every weekday", "Every 2 weeks", "Every month on the 31st". */
export function describeRule(rule: string | Rule | null | undefined): string {
	const r = typeof rule === "string" || rule == null ? parseRule(rule) : rule;
	if (!r) return "Does not repeat";
	const [one, many] = UNIT[r.freq];
	let s = r.interval === 1 ? `Every ${one}` : `Every ${r.interval} ${many}`;
	if (r.freq === "weekdays") s = r.interval === 1 ? "Every weekday" : `${s} on weekdays`;
	if (r.freq === "monthly" && r.byMonthDay) s += ` on the ${ordinal(r.byMonthDay)}`;
	return s;
}

/**
 * The next due date strictly after `from`. `rule` may be a canonical string
 * or a parsed Rule; throws if it is invalid or `from` is not YYYY-MM-DD.
 */
export function nextDueDate(rule: string | Rule, from: string): string {
	const r = typeof rule === "string" ? parseRule(rule) : rule;
	if (!r) throw new Error(`invalid recurrence rule: ${String(rule)}`);
	const day = toDayNumber(from);

	switch (r.freq) {
		case "daily":
			return fromDayNumber(day + r.interval);
		case "weekly":
			return fromDayNumber(day + 7 * r.interval);
		case "weekdays": {
			const weekday = isoWeekday(day); // 1 = Mon .. 7 = Sun
			if (weekday < 5) return fromDayNumber(day + 1); // Mon-Thu: the next day
			// Fri-Sun: Monday of the week `interval` weeks after this one.
			return fromDayNumber(day - (weekday - 1) + 7 * r.interval);
		}
		case "monthly": {
			const { y, m, d } = parts(from);
			const target = r.byMonthDay ?? d;
			const months = y * 12 + (m - 1) + r.interval;
			const ny = Math.floor(months / 12);
			const nm = (months % 12) + 1;
			return iso(ny, nm, Math.min(target, daysInMonth(ny, nm)));
		}
	}
}

// ---------------------------------------------------------------- day maths

const DAY_MS = 86_400_000;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parts(date: string): { y: number; m: number; d: number } {
	const match = DATE.exec(date);
	if (!match) throw new Error(`expected YYYY-MM-DD, got ${date}`);
	const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
	if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) throw new Error(`invalid date ${date}`);
	return { y, m, d };
}

function toDayNumber(date: string): number {
	const { y, m, d } = parts(date);
	return Date.UTC(y, m - 1, d) / DAY_MS;
}

function fromDayNumber(n: number): string {
	return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

function isoWeekday(dayNumber: number): number {
	// Day 0 (1970-01-01) was a Thursday (ISO 4).
	return ((((dayNumber + 3) % 7) + 7) % 7) + 1;
}

function daysInMonth(y: number, m: number): number {
	return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function iso(y: number, m: number, d: number): string {
	return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function ordinal(n: number): string {
	const rem100 = n % 100;
	if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
	return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}
